import { MeetingSlotsResponse, SpeakerProfile, MeetingSlot } from '../index';
import { HTML_TAG_REGEX, MULTIPLE_WHITESPACE_REGEX } from '../../constants';

type BookableMeetingAttendee = {
  attendeeId: number;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  biography: string | null;
  profileImageUrl: string | null;
};

type BookablePresenterOrganization = {
  organizationId: number;
  name: string;
  attendees: BookableMeetingAttendee[];
};

type BookableMeeting = {
  meetingId: number;
  startDateTime: string;
  endDateTime: string;
  timeZone: string;
  presenterOrganizations: BookablePresenterOrganization[];
  bookableStatus?: string;
};

type BookablePresenterGroup = {
  presenterIds: number[];
  meetings: BookableMeeting[];
};

const getGroups = (response: unknown): BookablePresenterGroup[] =>
  Array.isArray(response) ? (response as BookablePresenterGroup[]) : [];

const sanitizeBiography = (bio: string | null): string => {
  if (!bio) return '';

  try {
    if (typeof window !== 'undefined' && 'DOMParser' in window) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(bio, 'text/html');
      const text = doc.body.textContent || doc.body.innerText || '';
      return text.trim();
    }
  } catch {
   
  }

  return bio.replace(HTML_TAG_REGEX, '').replace(MULTIPLE_WHITESPACE_REGEX, ' ').trim();
};

const sortKeyForGroup = (group: BookablePresenterGroup): string => {
  const firstMeeting = group.meetings[0];
  if (!firstMeeting || !firstMeeting.presenterOrganizations) {
    return '';
  }

  const speakerNames = firstMeeting.presenterOrganizations.reduce<string[]>((acc, org) => {
    const orgNames = (org.attendees || []).map((att) => 
      `${att.firstName ?? ''} ${att.lastName ?? ''}`.trim()
    ).filter(Boolean);
    return [...acc, ...orgNames];
  }, []);

  return speakerNames.sort((a, b) => a.localeCompare(b)).join(' ').toLowerCase();
};

const getFirstSortedGroup = (groups: BookablePresenterGroup[]): BookablePresenterGroup | null => {
  if (!groups.length) {
    return null;
  }

  return groups.reduce((minGroup, currentGroup) => {
    const minKey = sortKeyForGroup(minGroup);
    const currentKey = sortKeyForGroup(currentGroup);
    return currentKey < minKey ? currentGroup : minGroup;
  });
};

const extractSpeakersFromGroup = (group: BookablePresenterGroup): SpeakerProfile[] => {
  const attendeeMap = group.meetings.reduce((map, meeting) => {
    meeting.presenterOrganizations.forEach((org) => {
      (org.attendees || []).forEach((att) => {
        if (!map.has(att.attendeeId)) {
          map.set(att.attendeeId, { attendee: att, organizationName: org.name });
        }
      });
    });
    return map;
  }, new Map<number, { attendee: BookableMeetingAttendee; organizationName: string }>());

  return Array.from(attendeeMap.values()).map(({ attendee, organizationName }) => ({
    id: String(attendee.attendeeId),
    name: `${attendee.firstName ?? ''} ${attendee.lastName ?? ''}`.trim(),
    title: attendee.title ?? '',
    organization: organizationName,
    profileImageUrl: attendee.profileImageUrl ?? '',
    biography: sanitizeBiography(attendee.biography)
  }));
};

const extractMeetingSlotsFromGroup = (group: BookablePresenterGroup): MeetingSlot[] => {
  return group.meetings.map((meeting) => ({
    id: String(meeting.meetingId),
    startDateTime: meeting.startDateTime,
    endDateTime: meeting.endDateTime,
    status: 'available' as const,
    bookableStatus: meeting.bookableStatus
  }));
};

const getEventTimeZoneFromGroup = (group: BookablePresenterGroup): string | undefined => {
  return group.meetings[0]?.timeZone;
};

const generateGroupId = (group: BookablePresenterGroup): string => {
  return group.presenterIds.sort((a, b) => a - b).join('-');
};

const generateGroupLabel = (group: BookablePresenterGroup): string => {
  const names = extractSpeakersFromGroup(group).map(s => s.name);
  if (names.length < 2) return names.join('');

  const last = names.pop();
  return `${names.join(',\n')}\n& ${last}`;
};


const sortedGroups = (groups: BookablePresenterGroup[]): BookablePresenterGroup[] => {
  const sortedData = groups.sort((a, b) => {
    const keyA = sortKeyForGroup(a);
    const keyB = sortKeyForGroup(b);
    return keyA.localeCompare(keyB);
  });

  return sortedData;
};

export const mapBookableMeetingGroupsToMeetingSlotsResponse = (
  response: unknown
): MeetingSlotsResponse => {
  const groups = getGroups(response);
  const firstGroup = getFirstSortedGroup(groups);
  const allGroups = sortedGroups(groups).map(group => ({
    id: generateGroupId(group),
    label: generateGroupLabel(group),
    speakers: extractSpeakersFromGroup(group),
    meetingSlots: extractMeetingSlotsFromGroup(group),
    eventTimeZone: getEventTimeZoneFromGroup(group)
  }));

  if (!firstGroup) {
    return {
      id: '',
      label: '',
      speakers: [],
      meetingSlots: []
    };
  }

  return {
    id: generateGroupId(firstGroup),
    label: generateGroupLabel(firstGroup),
    speakers: extractSpeakersFromGroup(firstGroup),
    meetingSlots: extractMeetingSlotsFromGroup(firstGroup),
    eventTimeZone: getEventTimeZoneFromGroup(firstGroup),
    groups: allGroups
  };
};


